import React, { useEffect, useRef } from 'react';

const HexMedicalAnimation = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animationFrameId;
        let width = window.innerWidth;
        let height = window.innerHeight;

        // Mouse state for interaction
        let mouse = { x: -1000, y: -1000 };

        // Configuration
        const HEX_SIZE = 40;
        const HEX_GAP = 5;
        const HEX_SPEED = 0.5; // Upward drift speed
        const WAVE_SPEED = 0.01;
        const MOUSE_RADIUS = 250;

        // --- Utils ---

        const drawHexagon = (ctx, x, y, size, strokeColor, lineWidth) => {
            ctx.beginPath();
            for (let i = 0; i < 6; i++) {
                const angle_deg = 60 * i - 30;
                const angle_rad = Math.PI / 180 * angle_deg;
                const px = x + size * Math.cos(angle_rad);
                const py = y + size * Math.sin(angle_rad);
                if (i === 0) ctx.moveTo(px, py);
                else ctx.lineTo(px, py);
            }
            ctx.closePath();
            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = lineWidth;
            ctx.stroke();
        };

        const drawIcon = (ctx, x, y, size) => {
            const iconSize = size * 0.5;
            ctx.save();
            ctx.translate(x, y);
            ctx.strokeStyle = "rgba(6, 182, 212, 0.9)"; // Cyan-500
            ctx.lineWidth = 1.5;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';

            ctx.beginPath();
            // Tech pulse line
            ctx.moveTo(-iconSize, 0);
            ctx.lineTo(-iconSize / 2, 0);
            ctx.lineTo(-iconSize / 4, -iconSize / 2);
            ctx.lineTo(iconSize / 4, iconSize / 2);
            ctx.lineTo(iconSize / 2, 0);
            ctx.lineTo(iconSize, 0);
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(0, 0, iconSize * 0.8, 0, Math.PI * 2);
            ctx.strokeStyle = "rgba(6, 182, 212, 0.3)";
            ctx.stroke();

            ctx.restore();
        };

        // --- Drawing Helpers for Medical Icons ---
        const drawPlus = (ctx, x, y, size) => {
            const s = size * 0.6;
            ctx.beginPath();
            ctx.moveTo(x - s, y);
            ctx.lineTo(x + s, y);
            ctx.moveTo(x, y - s);
            ctx.lineTo(x, y + s);
            ctx.stroke();
        };

        const drawRing = (ctx, x, y, size) => {
            ctx.beginPath();
            ctx.arc(x, y, size * 0.5, 0, Math.PI * 2);
            ctx.stroke();
        };

        const drawDiamond = (ctx, x, y, size) => {
            const s = size * 0.5;
            ctx.beginPath();
            ctx.moveTo(x, y - s);
            ctx.lineTo(x + s, y);
            ctx.lineTo(x, y + s);
            ctx.lineTo(x - s, y);
            ctx.closePath();
            ctx.stroke();
        };

        // --- Classes ---

        class HexGrid {
            constructor() {
                this.hexagons = [];
                this.offsetY = 0;
                this.rowHeight = 0;
                this.totalHeight = 0;
                this.init();
            }

            init() {
                this.hexagons = [];
                this.rowHeight = (HEX_SIZE + HEX_GAP) * Math.sqrt(3);
                const colWidth = (HEX_SIZE + HEX_GAP) * 1.5;

                const cols = Math.ceil((width * 0.45) / colWidth);
                const rows = Math.ceil(height / this.rowHeight) + 4;

                this.totalHeight = rows * this.rowHeight;

                for (let q = 0; q < cols; q++) {
                    for (let r = 0; r < rows; r++) {
                        const bx = q * colWidth + HEX_SIZE;
                        const by = r * this.rowHeight + (q % 2 === 1 ? this.rowHeight / 2 : 0);

                        const edgeThreshold = cols - 2;
                        let existsProbability = 1;
                        if (q > edgeThreshold) existsProbability = 0.3;
                        if (q > edgeThreshold + 1) existsProbability = 0.1;

                        if (Math.random() < existsProbability) {
                            const hasIcon = Math.random() < 0.25;
                            const opacity = Math.random() * 0.5 + 0.1;
                            this.hexagons.push({
                                baseX: bx, baseY: by,
                                x: bx, y: by,
                                dx: 0, dy: 0,
                                vx: 0, vy: 0,
                                hasIcon, opacity
                            });
                        }
                    }
                }
            }

            update(mouse) {
                // 1. Global scroll
                this.offsetY -= HEX_SPEED;
                if (this.offsetY < -this.totalHeight) {
                    this.offsetY += this.totalHeight;
                }

                // 2. Physics & Interaction
                // Spring constants
                const tension = 0.05;
                const damping = 0.9;
                const repulsionStrength = 1.0;

                this.hexagons.forEach(hex => {
                    // Calculate current visual position purely based on scroll first
                    let scrollY = (hex.baseY + this.offsetY) % this.totalHeight;
                    if (scrollY < -HEX_SIZE) scrollY += this.totalHeight;

                    // Check interaction with mouse
                    // We use the 'visual' position for distance check
                    const visualX = hex.baseX + hex.dx;
                    const visualY = scrollY + hex.dy;

                    const distX = mouse.x - visualX;
                    const distY = mouse.y - visualY;
                    const distance = Math.sqrt(distX * distX + distY * distY);

                    if (distance < MOUSE_RADIUS) {
                        const force = (MOUSE_RADIUS - distance) / MOUSE_RADIUS;
                        // Push away
                        const angle = Math.atan2(distY, distX);
                        const fx = Math.cos(angle) * force * repulsionStrength;
                        const fy = Math.sin(angle) * force * repulsionStrength;

                        hex.vx -= fx;
                        hex.vy -= fy;
                    }

                    // Spring force (return to dx=0, dy=0)
                    hex.vx += -tension * hex.dx;
                    hex.vy += -tension * hex.dy;

                    // Apply friction/damping
                    hex.vx *= damping;
                    hex.vy *= damping;

                    // Update displacement
                    hex.dx += hex.vx;
                    hex.dy += hex.vy;

                    // Update final visual coordinates for drawing
                    hex.x = hex.baseX + hex.dx;
                    hex.y = scrollY + hex.dy;
                });
            }

            draw(ctx) {
                this.hexagons.forEach(hex => {
                    // Optimization: offscreen culling
                    if (hex.y > height + HEX_SIZE || hex.y < -HEX_SIZE) return;

                    // Fade opacity to right
                    const fadeStart = width * 0.25;
                    const fadeEnd = width * 0.5;
                    let alphaX = 1;
                    if (hex.baseX > fadeStart) {
                        alphaX = 1 - (hex.baseX - fadeStart) / (fadeEnd - fadeStart);
                    }
                    if (alphaX < 0) alphaX = 0;

                    const finalAlpha = hex.opacity * alphaX;

                    if (finalAlpha > 0.01) {
                        const strokeColor = `rgba(6, 182, 212, ${finalAlpha})`;
                        // Use hex.x and hex.y which include physics displacement
                        drawHexagon(ctx, hex.x, hex.y, HEX_SIZE, strokeColor, 1);

                        if (hex.hasIcon) {
                            drawIcon(ctx, hex.x, hex.y, HEX_SIZE);
                        }
                    }
                });
            }
        }

        class DNAWave {
            constructor() {
                this.time = 0;
            }

            draw(ctx) {
                this.time += WAVE_SPEED;

                ctx.save();
                const gradient = ctx.createLinearGradient(0, 0, 0, height);
                gradient.addColorStop(0, "rgba(6, 182, 212, 0)");
                gradient.addColorStop(0.5, "rgba(6, 182, 212, 0.5)");
                gradient.addColorStop(1, "rgba(6, 182, 212, 0)");

                ctx.strokeStyle = gradient;
                ctx.lineWidth = 1;

                const numLines = 12;
                const baseX = width * 0.35;

                for (let j = 0; j < numLines; j++) {
                    ctx.beginPath();
                    const lineOffset = j * 4;

                    for (let y = 0; y < height; y += 10) {
                        const wave1 = Math.sin(y * 0.005 + this.time + j * 0.1) * 60;
                        const wave2 = Math.cos(y * 0.01 - this.time * 0.5) * 20;
                        const x = baseX + lineOffset + wave1 + wave2;

                        if (y === 0) ctx.moveTo(x, y);
                        else ctx.lineTo(x, y);
                    }
                    ctx.stroke();
                }

                const baseX2 = width * 0.25;
                for (let j = 0; j < 8; j++) {
                    ctx.beginPath();
                    const lineOffset = j * 3;
                    ctx.strokeStyle = `rgba(30, 64, 175, 0.3)`;

                    for (let y = 0; y < height; y += 15) {
                        const wave1 = Math.sin(y * 0.003 - this.time * 0.8 + Math.PI + j * 0.2) * 80;
                        const x = baseX2 + lineOffset + wave1;

                        if (y === 0) ctx.moveTo(x, y);
                        else ctx.lineTo(x, y);
                    }
                    ctx.stroke();
                }

                ctx.restore();
            }
        }

        class MedicalIcons {
            constructor() {
                this.icons = [];
                this.init();
            }

            init() {
                this.icons = [];
                // Reduced count slightly for clarity as icons are bigger than particles
                for (let i = 0; i < 40; i++) {
                    this.icons.push({
                        x: Math.random() * width,
                        y: Math.random() * height,
                        vx: (Math.random() - 0.5) * 0.5,
                        vy: (Math.random() - 0.5) * 0.5,
                        size: Math.random() * 5 + 3, // Larger size for icons
                        alpha: Math.random() * 0.4 + 0.1,
                        type: Math.floor(Math.random() * 3)
                    });
                }
            }

            updateAndDraw(ctx, mouse) {
                this.icons.forEach(p => {
                    // Base movement
                    p.x += p.vx;
                    p.y += p.vy;

                    // Wrap around screen
                    if (p.x < 0) p.x = width;
                    if (p.x > width) p.x = 0;
                    if (p.y < 0) p.y = height;
                    if (p.y > height) p.y = 0;

                    // Mouse Interaction (Repulsion only, no lines)
                    const dx = mouse.x - p.x;
                    const dy = mouse.y - p.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance < MOUSE_RADIUS) {
                        const force = (MOUSE_RADIUS - distance) / MOUSE_RADIUS;
                        const repulsionStrength = 2;
                        p.x -= (dx / distance) * force * repulsionStrength;
                        p.y -= (dy / distance) * force * repulsionStrength;
                    }

                    // Draw Icon
                    ctx.globalAlpha = p.alpha;
                    ctx.strokeStyle = 'rgba(200, 240, 255, 0.8)'; // Bright medical white-blue
                    ctx.lineWidth = 1;

                    if (p.type === 0) {
                        drawPlus(ctx, p.x, p.y, p.size);
                    } else if (p.type === 1) {
                        drawRing(ctx, p.x, p.y, p.size);
                    } else {
                        drawDiamond(ctx, p.x, p.y, p.size);
                    }
                });
                ctx.globalAlpha = 1;
            }
        }

        // --- Instantiation ---
        const hexGrid = new HexGrid();
        const dnaWave = new DNAWave();
        const medicalIcons = new MedicalIcons();

        // --- Animation Loop ---
        const render = () => {
            ctx.clearRect(0, 0, width, height);

            // 1. Draw Background Gradient
            const gradient = ctx.createRadialGradient(
                width * 0.9, height * 0.1, 0,
                width * 0.8, height * 0.2, width
            );
            gradient.addColorStop(0, '#004e92');
            gradient.addColorStop(0.3, '#020024');
            gradient.addColorStop(1, '#000000');

            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, width, height);

            // 2. Draw Layers
            hexGrid.update(mouse);
            hexGrid.draw(ctx);

            dnaWave.draw(ctx);

            medicalIcons.updateAndDraw(ctx, mouse);

            animationFrameId = requestAnimationFrame(render);
        };

        // Handle Resize
        const handleResize = () => {
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = width;
            canvas.height = height;
            hexGrid.init();
            medicalIcons.init();
        };

        // Handle Mouse Move
        const handleMouseMove = (e) => {
            const rect = canvas.getBoundingClientRect();
            mouse.x = e.clientX - rect.left;
            mouse.y = e.clientY - rect.top;
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('mousemove', handleMouseMove);

        // Init
        handleResize();
        render();

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('mousemove', handleMouseMove);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return <canvas ref={canvasRef} className="fixed inset-0 w-full h-full" style={{ zIndex: 0 }} />;
};

export default HexMedicalAnimation;
